import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { AuthService, BaseComponent } from '@ridango/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    CustomerService,
    LoginFacade,
    NotificationFacade
} from '@ridango/components';
import { AuthParametersFacade } from '@ridango/components/lib/auth/+state/auth-parameters';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';

import { take } from 'rxjs/operators';
import { FirebaseService } from '../../services/firebase.service';
import { Capacitor } from '@capacitor/core';
import { OnboardingType, TransferAnonymousDataModalComponent } from '@ridango/widgets';
import { AuthInterceptor, DeviceIdService, OnboardingService } from '@ridango/core/lib/services';
import Env from '@ridango/core/env';
import { NgRedux } from '@angular-redux/store';
import { AccountTicketsService } from '@ridango/components/lib/account-tickets-ngrx/services/account-tickets.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { RequestPermissionService } from '@ridango/components/lib/firebase/request-permission.service';
import { takeUntil } from 'rxjs/operators';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { AppState } from '../../app.store';
import { environment } from '../../../environments/environment';
import { LoginPageUtils } from './login-page.util';
@Component({
    templateUrl: './login-page.component.html',
    styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly captchaToggleAllowed = environment.captchaToggleAllowed;

    themeFeature = ThemeFeature;
    passwordReset = false;
    email = '';
    callback: string;
    allowLogin = true;
    redirectFromQuery?: string;
    showBackButton = false;
    isFooterExist = false;
    isAlertExist = false;
    termsModalVisible = false;
    termsUrl: string;
    terms = null;
    termsModalVisible$ = this.loginFacade.termsModalVisible$;

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject('ENV') public env: Env,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly notification: NotificationFacade,
        private readonly loginFacade: LoginFacade,
        private readonly route: ActivatedRoute,
        private readonly authParams: AuthParametersFacade,
        private readonly firebaseService: FirebaseService,
        private readonly onboardingService: OnboardingService,
        private readonly analyticsService: AnalyticsService,
        private readonly store: NgRedux<any>,
        private readonly accountTicketsService: AccountTicketsService,
        private readonly accountTicketsFacade: AccountTicketsNgrxFacade,
        private readonly deviceIdService: DeviceIdService,
        private readonly requestPermissionService: RequestPermissionService,
        private readonly authInterceptor: AuthInterceptor,
        private readonly authService: AuthService,
        private readonly ngRedux: NgRedux<AppState>,
        private readonly applicationFacade: ApplicationFacade,
        private readonly customerService: CustomerService
    ) {
        super();
    }

    ngOnInit() {
        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            const selectedLanguage = this.translate.currentLang;
            this.terms = this.ngRedux.getState().core.region.terms;
            if (this.terms) {
                this.termsUrl = this.authService.buildTermsIframeUrl(this.terms?.contentBaseUrl, selectedLanguage);
            }
        }

        this.authService.currentTermsToAccept$.pipe(takeUntil(this.componentDestroyed$)).subscribe((newTerms) => {
            const selectedLanguage = this.translate.currentLang;
            this.terms = newTerms;

            if (this.terms) {
                this.termsUrl = this.authService.buildTermsIframeUrl(this.terms?.contentBaseUrl, selectedLanguage);
            }
        });

        this.authService.acceptedToLatestTerms$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            const token = this.authService.getLocalAccessToken();
            this.finishLogin(token);
        });

        if (this.env.SIGN_ON_PAGE) {
            this.handleCallbackURL();
        }

        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            if (params.redirect) {
                this.redirectFromQuery = params.redirect;
            }
        });

        if (this.env.IS_MOBILE_APP) {
            this.onboardingService.onboardingStatusValue
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((onboardingStatus: OnboardingType) => {
                    const showBackButton = this.route.snapshot.queryParams['backButton'];

                    if (showBackButton) {
                        this.showBackButton = true;
                        return;
                    }

                    this.showBackButton =
                        ![
                            OnboardingType.COMPLETED,
                            OnboardingType.STARTED,
                            OnboardingType.ONBOARD_FLOW,
                            OnboardingType.LOGIN_FLOW
                        ].includes(onboardingStatus) && !!this.env.ONBOARDING.LOGIN.REQUIRE_LOGIN;
                });
        }
    }

    ngAfterViewInit() {
        if (this.env.IS_MOBILE_APP) {
            setTimeout(() => {
                this.isFooterExist = !!document.getElementsByTagName('gl-footer-app')[0];
                this.isAlertExist = !!document.getElementsByTagName('rwidget-alert')[0]?.children?.length;
            }, 100);
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.loginFacade.loginCleanUp();
    }

    handlePasswordReset(flag: boolean) {
        this.passwordReset = flag;
    }

    submitEmail(email: string) {
        this.email = email;
    }

    handleCallbackURL() {
        this.authParams.callback$.pipe(take(2)).subscribe((callback) => {
            if (callback) {
                this.callback = callback;
            }
        });
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            if (params.callback) {
                this.authParams.setCallback(params.callback);
            }
        });
        if (!this.callback) {
            this.allowLogin = false;
            this.notification.error(this.translate.instant('CALLBACK_MISSING'));
        }
    }

    handleLogin(token: string) {
        this.notification.success(this.translate.instant('LOCAL_LOGIN_SUCCESS'), {
            title: this.translate.instant('LOCAL_LOGIN_SUCCESS_TITLE'),
            showAfterRouteChange: true
        });

        this.authInterceptor.restartUserTokenRefresh();

        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.LOGIN,
            name: 'email'
        });

        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            // If user has agreed to terms on onboarding, we can proceed with login
            if (this.store.getState().application.userAgreedTermsVersionOnOnboarding) {
                this.authService
                    .fetchLatestTerms()
                    .pipe(take(1))
                    .subscribe((terms) => {
                        // If latest terms version is different from the one user agreed to on onboarding
                        // Note: It won't let to accept to older version of terms
                        if (
                            (terms as any)?.version !==
                            this.store.getState().application.userAgreedTermsVersionOnOnboarding
                        ) {
                            this.loginFacade.showTermsModal();
                        } else {
                            this.authService
                                .agreeToLatestTerms(
                                    this.store.getState().application.userAgreedTermsVersionOnOnboarding
                                )
                                .pipe(take(1))
                                .subscribe(() => {
                                    this.applicationFacade.setUserAgreedTermsVersionOnOnboarding(null);
                                    this.finishLogin(token);
                                });
                        }
                    });
            } else {
                this.authService
                    .hasUserAgreedToLatest()
                    .pipe(take(1))
                    .subscribe((hasAgreed) => {
                        if (!hasAgreed) {
                            this.authService
                                .fetchLatestTerms()
                                .pipe(take(1))
                                .subscribe(() => {
                                    this.loginFacade.showTermsModal();
                                });
                        } else {
                            this.finishLogin(token);
                        }
                    });
            }
        } else {
            this.finishLogin(token);
        }
    }

    private finishLogin(token: string) {
        this.loginFacade.hideTermsModal();
        if (this.env.APP_NOTIFICATIONS?.ENABLED && Capacitor.isNativePlatform()) {
            if (this.env.NOTIFICATION_PAGE_USER) {
                this.requestPermissionService.requestPermissionLogin();
            } else {
                this.firebaseService.registerFcmSdkToken();
            }
        }

        if (this.deviceIdService.getAccessDeviceToken()) {
            this.accountTicketsService
                .transferAnonymousData(TransferAnonymousDataModalComponent)
                .pipe(take(1))
                .subscribe();
        }

        if (token && this.callback) {
            const callback = this.callback;

            this.authParams.reset();
            this.document.location.href = `${callback}?accessToken=${token}`;
        } else {
            this.navigateAfterLogin();
        }
        if (this.env.IS_MOBILE_APP) {
            this.accountTicketsFacade.resetAccountTickets();
            this.onboardingService.onboardingStatus = OnboardingType.COMPLETED;
        }
    }

    handleBackClicked() {
        window.history.back();
    }

    private navigateAfterLogin(): void {
        if (!LoginPageUtils.hasOidcSignupCompleteConfig(this.env.REGISTRATION)) {
            this.navigateToDefaultPostLoginRoute();
            return;
        }

        this.customerService
            .fetchCustomerProfile()
            .pipe(take(1))
            .subscribe({
                next: (customerProfileResponse) => {
                    this.navigateToOidcSignupCompleteOrDefault(customerProfileResponse);
                },
                error: () => {
                    const fallbackProfileData = this.ngRedux.getState().customer?.profile ?? {};
                    this.navigateToOidcSignupCompleteOrDefault(fallbackProfileData);
                }
            });
    }

    private navigateToOidcSignupCompleteOrDefault(profileData: Record<string, unknown> | null | undefined): void {
        const authCustomerData = this.ngRedux.getState().auth?.customer?.data ?? {};
        if (LoginPageUtils.shouldNavigateToOidcSignupComplete(this.env.REGISTRATION, authCustomerData, profileData)) {
            this.router.navigate([this.translate.currentLang, 'signup-complete']);
            return;
        }
        this.navigateToDefaultPostLoginRoute();
    }

    private navigateToDefaultPostLoginRoute(): void {
        if (this.env.IS_CORPORATE) {
            this.router.navigate([this.translate.currentLang, 'account', 'switch-company'], {
                queryParams: { l: 't' }
            });
            return;
        }

        this.router.navigate([this.translate.currentLang, this.redirectFromQuery ?? 'account']);
    }
}
